import { AnyAction, Middleware } from 'redux';
import {
  featuresActionTypes,
  featuresActions,
  featuresSelectors,
} from '../modules/features';
import { asyncActionSuffixes } from '../util/constants';
import {
  readStoredFeatureToggleOverrides,
  setStoredFeatureToggleOverride,
  writeStoredFeatureToggleOverrides,
} from '../../util/featureToggleLocalOverrides';

function shouldSkipPersist(action: {
  meta?: { skipLocalPersist?: boolean };
}): boolean {
  return action.meta?.skipLocalPersist === true;
}

function pruneOverridesToKnownFeatures(
  overrides: Record<string, boolean>,
  knownFeatures: Record<string, boolean>,
): Record<string, boolean> {
  const pruned: Record<string, boolean> = {};
  for (const key of Object.keys(knownFeatures)) {
    if (Object.prototype.hasOwnProperty.call(overrides, key)) {
      pruned[key] = overrides[key];
    }
  }
  return pruned;
}

export function createFeatureToggleLocalOverridesMiddleware(): Middleware {
  return store => next => action => {
    const result = next(action);

    const type = action.type;

    const fetchSuccessTypes = [
      featuresActionTypes.FETCH_FEATURES + asyncActionSuffixes.success,
      featuresActionTypes.FETCH_UNAUTH_FEATURES + asyncActionSuffixes.success,
    ] as string[];

    if (fetchSuccessTypes.includes(type)) {
      const apiFeatures = featuresSelectors.selectFeatures(store.getState());
      const stored = readStoredFeatureToggleOverrides();
      const pruned = pruneOverridesToKnownFeatures(stored, apiFeatures);
      if (JSON.stringify(stored) !== JSON.stringify(pruned)) {
        writeStoredFeatureToggleOverrides(pruned);
      }

      const toApply = Object.entries(pruned).map(([feature, value]) => ({
        feature,
        value,
      }));
      if (toApply.length > 0) {
        store.dispatch(
          featuresActions.toggleFeatures(toApply, {
            skipLocalPersist: true,
          }) as unknown as AnyAction,
        );
      }
      return result;
    }

    if (shouldSkipPersist(action)) {
      return result;
    }

    if (type === featuresActionTypes.TOGGLE_FEATURE) {
      const a = action as ReturnType<typeof featuresActions.toggleFeature>;
      const feats = featuresSelectors.selectFeatures(store.getState());
      if (Object.prototype.hasOwnProperty.call(feats, a.feature)) {
        setStoredFeatureToggleOverride(a.feature, feats[a.feature]);
      }
    }

    if (type === featuresActionTypes.TOGGLE_FEATURES) {
      const a = action as ReturnType<typeof featuresActions.toggleFeatures>;
      const feats = featuresSelectors.selectFeatures(store.getState());
      a.features.forEach(f => {
        if (Object.prototype.hasOwnProperty.call(feats, f.feature)) {
          setStoredFeatureToggleOverride(f.feature, feats[f.feature]);
        }
      });
    }

    return result;
  };
}
